// SPDX-License-Identifier: MIT
//
// 2026-05-19 Phase X.A — "Connect AI" panel. Sits at the top of /welcome,
// BEFORE the Magnify composer. Newbie flow :
//   1. Sign in to Genesis
//   2. Land on /welcome → THIS panel asks for the user's Anthropic API key
//      (with inline tutorial for users without a console.anthropic.com
//      account yet)
//   3. Save → all subsequent Magnify / Discover calls use the user's key
//      (their billing on Anthropic side, not Genesis-paid)
//
// 2026-05-25 Point A — on DESKTOP (allowSovereign) the panel also offers a
// "sovereign endpoint" kind : the user connects an OpenAI-compatible endpoint
// (ai-lab's vLLM / LiteLLM) + a model alias + an optional local key. The save
// goes to the desktop-local store (not Supabase) ; magnify then routes there.
// Web never passes allowSovereign, so its UI is byte-identical to before.
//
// Two visual states, mirroring the BYO backend panel from pt12 :
// - Empty (no key) : expanded form with input(s) + Save button + tutorial details
// - Wired (key set) : collapsed summary with masked key + Edit/Disconnect

import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import {
  type UserAiProvider,
  maskAnthropicKey,
} from '../auth/ai-provider';
import { validateSovereignConfig, type SovereignConfig } from '../auth/provider-config';

export interface ConnectAiOptions {
  current: UserAiProvider | null;
  onSave: (apiKey: string) => Promise<void>;
  onDisconnect: () => Promise<void>;
  // Called after a successful save or disconnect so the parent can re-
  // render dependent surfaces (composer enabled/disabled, banner removed).
  onStateChange?: () => void;
  // Point A (desktop only): when set, the panel shows a provider-kind selector
  // and a sovereign-endpoint form. onSaveSovereign persists the config locally.
  allowSovereign?: boolean;
  onSaveSovereign?: (cfg: SovereignConfig) => Promise<void>;
  // A previously-saved sovereign config (read from the desktop-local store on
  // mount) → renders the sovereign-wired state so a configured endpoint shows
  // as connected on reload, not as an empty form.
  currentSovereign?: SovereignConfig | null;
}

export function renderConnectAi(opts: ConnectAiOptions): HTMLElement {
  const wrap = document.createElement('section');
  wrap.className = 'connect-ai';
  wrap.setAttribute('data-component', 'connect-ai');

  if (opts.currentSovereign) {
    renderSovereignWired(wrap, opts, opts.currentSovereign);
  } else if (opts.current) {
    renderWired(wrap, opts);
  } else {
    renderEmpty(wrap, opts);
  }
  return wrap;
}

function renderWired(wrap: HTMLElement, opts: ConnectAiOptions): void {
  const masked = maskAnthropicKey(opts.current!.api_key);
  wrap.innerHTML = `
    <details class="connect-ai__details" data-state="wired">
      <summary class="connect-ai__summary">
        <span class="connect-ai__badge connect-ai__badge--wired">🤖 ${escapeHtml(t('connectAi.wiredBadge'))}</span>
        <span class="connect-ai__masked"><code>${escapeHtml(masked)}</code></span>
        <span class="connect-ai__hint">${escapeHtml(t('connectAi.wiredHint'))}</span>
      </summary>
      <div class="connect-ai__body">
        <p class="connect-ai__wired-line">${escapeHtml(t('connectAi.wiredDetail'))}</p>
        <div class="connect-ai__actions">
          <button type="button" data-component="connect-ai-edit" class="connect-ai__edit-btn">${escapeHtml(t('connectAi.editBtn'))}</button>
          <button type="button" data-component="connect-ai-disconnect" class="connect-ai__disconnect-btn">${escapeHtml(t('connectAi.disconnectBtn'))}</button>
        </div>
      </div>
    </details>
  `;
  wrap.querySelector<HTMLButtonElement>('[data-component="connect-ai-edit"]')?.addEventListener('click', () => {
    renderEmpty(wrap, opts);
  });
  wrap.querySelector<HTMLButtonElement>('[data-component="connect-ai-disconnect"]')?.addEventListener('click', async () => {
    const btn = wrap.querySelector<HTMLButtonElement>('[data-component="connect-ai-disconnect"]')!;
    btn.disabled = true;
    btn.textContent = t('connectAi.disconnectBusy');
    try {
      await opts.onDisconnect();
      renderEmpty(wrap, { ...opts, current: null });
      opts.onStateChange?.();
    } catch {
      btn.textContent = t('connectAi.disconnectError');
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = t('connectAi.disconnectBtn');
      }, 2400);
    }
  });
}

function renderEmpty(wrap: HTMLElement, opts: ConnectAiOptions): void {
  const showSelector = !!(opts.allowSovereign && opts.onSaveSovereign);
  const selector = showSelector ? `
      <div class="connect-ai__kinds" data-component="connect-ai-kind-selector" role="tablist">
        <button type="button" role="tab" class="connect-ai__kind" data-component="connect-ai-kind-anthropic" data-active="true">${escapeHtml(t('connectAi.kindAnthropic'))}</button>
        <button type="button" role="tab" class="connect-ai__kind" data-component="connect-ai-kind-sovereign" data-active="false">${escapeHtml(t('connectAi.kindSovereign'))}</button>
      </div>` : '';
  wrap.innerHTML = `
    <div class="connect-ai__empty">
      <div class="connect-ai__head">
        <span class="connect-ai__badge connect-ai__badge--empty">🤖 ${escapeHtml(t('connectAi.emptyBadge'))}</span>
        <h3 class="connect-ai__title">${escapeHtml(t('connectAi.title'))}</h3>
      </div>
      ${selector}
      <div class="connect-ai__mode" data-component="connect-ai-mode"></div>
    </div>
  `;
  const mode = wrap.querySelector<HTMLElement>('[data-component="connect-ai-mode"]')!;

  const setMode = (kind: 'anthropic' | 'sovereign'): void => {
    if (showSelector) {
      wrap.querySelector('[data-component="connect-ai-kind-anthropic"]')?.setAttribute('data-active', String(kind === 'anthropic'));
      wrap.querySelector('[data-component="connect-ai-kind-sovereign"]')?.setAttribute('data-active', String(kind === 'sovereign'));
    }
    if (kind === 'sovereign') {
      mode.innerHTML = sovereignFormMarkup();
      wireSovereign(wrap, mode, opts);
    } else {
      mode.innerHTML = anthropicFormMarkup();
      wireAnthropic(wrap, mode, opts);
    }
  };

  if (showSelector) {
    wrap.querySelector('[data-component="connect-ai-kind-anthropic"]')?.addEventListener('click', () => setMode('anthropic'));
    wrap.querySelector('[data-component="connect-ai-kind-sovereign"]')?.addEventListener('click', () => setMode('sovereign'));
  }
  setMode('anthropic');
}

function anthropicFormMarkup(): string {
  return `
      <p class="connect-ai__intro">${escapeHtml(t('connectAi.intro'))}</p>
      <label class="connect-ai__field">
        <span class="connect-ai__field-label">${escapeHtml(t('connectAi.keyLabel'))}</span>
        <input type="text" data-component="connect-ai-key-input" class="connect-ai__input" placeholder="sk-ant-api03-..." autocomplete="off" spellcheck="false">
        <small class="connect-ai__field-error" data-component="connect-ai-error" hidden></small>
      </label>
      <div class="connect-ai__actions">
        <button type="button" data-component="connect-ai-save" class="connect-ai__save-btn" data-state="idle">
          <span data-component="connect-ai-save-label">${escapeHtml(t('connectAi.saveBtn'))}</span>
        </button>
      </div>
      <details class="connect-ai__tutorial">
        <summary class="connect-ai__tutorial-summary">${escapeHtml(t('connectAi.tutorialSummary'))}</summary>
        <div class="connect-ai__tutorial-body">
          <ol class="connect-ai__tutorial-list">
            <li>${t('connectAi.tutorialStep1').replace(
              '{link}',
              `<a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" class="connect-ai__tutorial-link">${escapeHtml(t('connectAi.tutorialLink1'))}</a>`,
            )}</li>
            <li>${escapeHtml(t('connectAi.tutorialStep2'))}</li>
            <li>${t('connectAi.tutorialStep3').replace(
              '{link}',
              `<a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" class="connect-ai__tutorial-link">${escapeHtml(t('connectAi.tutorialLink3'))}</a>`,
            )}</li>
            <li>${escapeHtml(t('connectAi.tutorialStep4'))}</li>
          </ol>
          <p class="connect-ai__tutorial-note">${escapeHtml(t('connectAi.tutorialNote'))}</p>
        </div>
      </details>
      <p class="connect-ai__footnote">${escapeHtml(t('connectAi.footnote'))}</p>
  `;
}

function sovereignFormMarkup(): string {
  return `
      <p class="connect-ai__intro">${escapeHtml(t('connectAi.sovereignIntro'))}</p>
      <label class="connect-ai__field">
        <span class="connect-ai__field-label">${escapeHtml(t('connectAi.endpointLabel'))}</span>
        <input type="text" data-component="connect-ai-endpoint-input" class="connect-ai__input" placeholder="http://localhost:8000/v1" autocomplete="off" spellcheck="false">
      </label>
      <label class="connect-ai__field">
        <span class="connect-ai__field-label">${escapeHtml(t('connectAi.modelLabel'))}</span>
        <input type="text" data-component="connect-ai-model-input" class="connect-ai__input" placeholder="sovereign-default" autocomplete="off" spellcheck="false">
      </label>
      <label class="connect-ai__field">
        <span class="connect-ai__field-label">${escapeHtml(t('connectAi.sovKeyLabel'))}</span>
        <input type="password" data-component="connect-ai-sovkey-input" class="connect-ai__input" placeholder="••••••• (optionnel)" autocomplete="off" spellcheck="false">
        <small class="connect-ai__field-error" data-component="connect-ai-sov-error" hidden></small>
      </label>
      <div class="connect-ai__actions">
        <button type="button" data-component="connect-ai-sov-save" class="connect-ai__save-btn" data-state="idle">
          <span data-component="connect-ai-sov-save-label">${escapeHtml(t('connectAi.saveBtn'))}</span>
        </button>
      </div>
      <p class="connect-ai__footnote">${escapeHtml(t('connectAi.sovereignNote'))}</p>
  `;
}

function wireAnthropic(wrap: HTMLElement, scope: HTMLElement, opts: ConnectAiOptions): void {
  const input = scope.querySelector<HTMLInputElement>('[data-component="connect-ai-key-input"]')!;
  const error = scope.querySelector<HTMLElement>('[data-component="connect-ai-error"]')!;
  const saveBtn = scope.querySelector<HTMLButtonElement>('[data-component="connect-ai-save"]')!;
  const saveLabel = scope.querySelector<HTMLElement>('[data-component="connect-ai-save-label"]')!;

  // Mirror the DB CHECK constraint user_ai_providers_anthropic_key_shape :
  // starts with `sk-ant-`, ≥ 80 chars. Server is the source of truth ;
  // client validation just speeds up the typo / "wrong-paste" feedback loop.
  const validate = (v: string): string | null => {
    if (!v) return t('connectAi.errorRequired');
    if (!v.startsWith('sk-ant-')) return t('connectAi.errorPrefix');
    if (v.length < 80) return t('connectAi.errorTooShort');
    return null;
  };
  const showErr = (msg: string | null): void => {
    if (msg) { error.textContent = msg; error.hidden = false; }
    else { error.textContent = ''; error.hidden = true; }
  };

  saveBtn.addEventListener('click', async () => {
    if (saveBtn.getAttribute('data-state') !== 'idle') return;
    const key = input.value.trim();
    const e = validate(key);
    showErr(e);
    if (e) return;

    saveBtn.setAttribute('data-state', 'busy');
    saveLabel.textContent = t('connectAi.saveBusy');
    try {
      await opts.onSave(key);
      renderWired(wrap, {
        ...opts,
        current: {
          user_id: '',  // overwritten by parent's row refetch on next mount
          provider_kind: 'anthropic',
          api_key: key,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      });
      opts.onStateChange?.();
    } catch (err) {
      console.warn('[connect-ai] save failed', err);
      saveBtn.setAttribute('data-state', 'error');
      saveLabel.textContent = t('connectAi.saveError');
      setTimeout(() => {
        saveBtn.setAttribute('data-state', 'idle');
        saveLabel.textContent = t('connectAi.saveBtn');
      }, 2400);
    }
  });
}

function wireSovereign(wrap: HTMLElement, scope: HTMLElement, opts: ConnectAiOptions): void {
  const endpoint = scope.querySelector<HTMLInputElement>('[data-component="connect-ai-endpoint-input"]')!;
  const model = scope.querySelector<HTMLInputElement>('[data-component="connect-ai-model-input"]')!;
  const keyInput = scope.querySelector<HTMLInputElement>('[data-component="connect-ai-sovkey-input"]')!;
  const error = scope.querySelector<HTMLElement>('[data-component="connect-ai-sov-error"]')!;
  const saveBtn = scope.querySelector<HTMLButtonElement>('[data-component="connect-ai-sov-save"]')!;
  const saveLabel = scope.querySelector<HTMLElement>('[data-component="connect-ai-sov-save-label"]')!;

  const showErr = (msg: string | null): void => {
    if (msg) { error.textContent = msg; error.hidden = false; }
    else { error.textContent = ''; error.hidden = true; }
  };
  const errFor = (field: 'endpoint_url' | 'model', code: string): string => {
    if (field === 'endpoint_url') return code === 'required' ? t('connectAi.sovEndpointRequired') : t('connectAi.sovEndpointInvalid');
    return t('connectAi.sovModelRequired');
  };

  saveBtn.addEventListener('click', async () => {
    if (saveBtn.getAttribute('data-state') !== 'idle') return;
    const res = validateSovereignConfig({
      endpoint_url: endpoint.value,
      model: model.value,
      api_key: keyInput.value,
    });
    if (!res.ok) { showErr(errFor(res.field, res.code)); return; }
    showErr(null);

    saveBtn.setAttribute('data-state', 'busy');
    saveLabel.textContent = t('connectAi.saveBusy');
    try {
      await opts.onSaveSovereign!(res.value);
      renderSovereignWired(wrap, opts, res.value);
      opts.onStateChange?.();
    } catch (err) {
      console.warn('[connect-ai] sovereign save failed', err);
      saveBtn.setAttribute('data-state', 'error');
      saveLabel.textContent = t('connectAi.saveError');
      setTimeout(() => {
        saveBtn.setAttribute('data-state', 'idle');
        saveLabel.textContent = t('connectAi.saveBtn');
      }, 2400);
    }
  });
}

function renderSovereignWired(wrap: HTMLElement, opts: ConnectAiOptions, cfg: SovereignConfig): void {
  let host = cfg.endpoint_url;
  try { host = new URL(cfg.endpoint_url).host; } catch { /* keep raw */ }
  wrap.innerHTML = `
    <details class="connect-ai__details" data-state="wired" data-kind="sovereign">
      <summary class="connect-ai__summary">
        <span class="connect-ai__badge connect-ai__badge--wired">🛰️ ${escapeHtml(t('connectAi.sovWiredBadge'))}</span>
        <span class="connect-ai__masked"><code>${escapeHtml(host)}</code> · <code>${escapeHtml(cfg.model)}</code></span>
      </summary>
      <div class="connect-ai__body">
        <p class="connect-ai__wired-line">${escapeHtml(t('connectAi.sovWiredDetail'))}</p>
        <div class="connect-ai__actions">
          <button type="button" data-component="connect-ai-sov-edit" class="connect-ai__edit-btn">${escapeHtml(t('connectAi.editBtn'))}</button>
        </div>
      </div>
    </details>
  `;
  wrap.querySelector<HTMLButtonElement>('[data-component="connect-ai-sov-edit"]')?.addEventListener('click', () => {
    renderEmpty(wrap, opts);
  });
}
